import { EMOJI_COPIES, type CopyPath } from "./burst.ts";
import { EMOJI_POOL_SIZE } from "./pool.ts";

/**
 * The ring of copies every pop shares (ADR-114's amendment of 2026-10-09, point 6): `COPY_RING_SIZE` sprites, made
 * together at the first pop and never more. A pop claims the next slots in order, wrapping past the end, and marks each
 * with its token, so when the ring is full the slots taken are the oldest pop's, whose copies are the faintest on
 * screen; a pop owns a slot while the slot still holds its token. Each slot's path lives in typed arrays made once, so
 * claiming and writing allocate nothing.
 *
 * @example
 * ```ts
 * const ring = new CopyRing(() => makeSprite());
 * const first = ring.claim(EMOJI_COPIES, token);
 * ring.write(first, copyPath(handle, pop, 0, path));
 * if (ring.owns(first, token)) draw(ring.sprite(first));
 * ```
 */

/**
 * How many copies are drawn at once at most: every copy of the most emoji the pool holds at once. The pool ends an
 * emoji's copies when it ends or its sprite is taken, so whatever the server sends no live copy is ever taken.
 */
export const COPY_RING_SIZE = EMOJI_POOL_SIZE * EMOJI_COPIES;

/**
 * A ring of copy sprites and their paths.
 */
export class CopyRing<T> {
  public readonly cos: Float32Array;
  public readonly sin: Float32Array;
  public readonly reach: Float32Array;
  public readonly drift: Float32Array;
  public readonly wait: Uint8Array;
  public readonly life: Uint8Array;
  private readonly sprites: T[] = [];
  private readonly tokens: Int32Array;
  private next = 0;

  /**
   * @param create Makes a sprite; called for every slot at the first claim.
   * @param size The ring's slots.
   */
  public constructor(
    private readonly create: () => T,
    public readonly size: number = COPY_RING_SIZE,
  ) {
    this.cos = new Float32Array(size);
    this.sin = new Float32Array(size);
    this.reach = new Float32Array(size);
    this.drift = new Float32Array(size);
    this.wait = new Uint8Array(size);
    this.life = new Uint8Array(size);
    this.tokens = new Int32Array(size);
  }

  /**
   * Sprites made so far: none before the first claim, every slot's after it.
   *
   * @returns The count.
   */
  public get created(): number {
    return this.sprites.length;
  }

  /**
   * Claims the next slots for a pop, taking them from whichever pop held them.
   *
   * @param count How many copies the pop draws.
   * @param token The pop's token, which no other live pop holds.
   * @returns The first slot; the pop's slots run on from it, wrapping past the end.
   * @throws RangeError when the count is not a whole number from 1 to the ring's size, or the token not a whole number
   *   above 0 that fits 32 bits.
   */
  public claim(count: number, token: number): number {
    if (!Number.isInteger(count) || count < 1 || count > this.size) {
      throw new RangeError(`a pop claims a whole number of copies from 1 to ${this.size}, not ${count}`);
    }
    if (!Number.isInteger(token) || token < 1 || token > 0x7fffffff) {
      throw new RangeError(`a pop's token must be a whole number from 1 to 2^31 - 1, not ${token}`);
    }
    while (this.sprites.length < this.size) {
      this.sprites.push(this.create());
    }
    const first = this.next;
    for (let index = 0; index < count; index += 1) {
      this.tokens[(first + index) % this.size] = token;
    }
    this.next = (first + count) % this.size;
    return first;
  }

  /**
   * Whether a pop still owns a slot.
   *
   * @param slot The slot.
   * @param token The pop's token.
   * @returns True while no later pop has taken it.
   */
  public owns(slot: number, token: number): boolean {
    return this.tokens[slot] === token;
  }

  /**
   * A slot's sprite.
   *
   * @param slot The slot, after the first claim.
   * @returns The sprite.
   */
  public sprite(slot: number): T {
    return this.sprites[slot] as T;
  }

  /**
   * Writes a slot's path into the ring's arrays.
   *
   * @param slot The slot.
   * @param path The path.
   */
  public write(slot: number, path: Readonly<CopyPath>): void {
    this.cos[slot] = path.cos;
    this.sin[slot] = path.sin;
    this.reach[slot] = path.reach;
    this.drift[slot] = path.drift;
    this.wait[slot] = path.wait;
    this.life[slot] = path.life;
  }
}
